// retryWithBackoff — small helper for the fork→snapshot→publish flow.
//
// Why this exists in its own module: the curated-playbook fork
// (`ForkAndPublishButton`) chains THREE network-y operations
// (snapshot → publish → mastery upsert) and the post-fork dialog
// (`ForkSkillsConfirmDialog`) does a publish. Any one of these can
// transiently fail on flaky networks or cold edge-function starts.
// Without retries the user sees "Publish failed" and has to manually
// kick off the entire flow again — which means re-snapshotting and
// potentially creating a duplicate `guide_history` row.
//
// The helper only retries errors that *look* transient (network /
// 5xx / timeout / abort). Validation, auth and quota errors bail
// immediately so we don't punish the user with a long spinner before
// a guaranteed failure.

/** Errors whose messages contain any of these substrings are treated
 *  as retryable. Kept lowercase + small on purpose — false negatives
 *  (no retry) are safer than false positives (looping a 4xx). */
const RETRYABLE_HINTS = [
  "fetch",
  "network",
  "timeout",
  "timed out",
  "abort",
  "503",
  "502",
  "504",
  "econnreset",
  "socket",
  "failed to send",
  "failed to fetch",
];

/** Status codes we treat as worth retrying. 408 = request timeout,
 *  425 = too early, 429 = rate-limit (we honour it but back off),
 *  5xx = server-side hiccup. */
const RETRYABLE_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);

export function isRetryableError(err: unknown): boolean {
  if (!err) return false;
  const e = err as { status?: number; statusCode?: number; message?: string; name?: string };
  const status = e.status ?? e.statusCode;
  if (typeof status === "number" && RETRYABLE_STATUS.has(status)) return true;
  if (e.name === "AbortError") return true;
  const msg = (e.message ?? String(err)).toLowerCase();
  return RETRYABLE_HINTS.some((h) => msg.includes(h));
}

export interface RetryOptions {
  /** Total attempts including the first try. Default 3. */
  maxAttempts?: number;
  /** Initial delay before the second attempt, in ms. Default 400. */
  baseDelayMs?: number;
  /** Cap on any single delay, in ms. Default 4000. */
  maxDelayMs?: number;
  /** Optional callback fired right before each retry sleep — useful
   *  for surfacing "Retrying… (attempt N/M)" toasts. */
  onRetry?: (info: { attempt: number; nextAttempt: number; delayMs: number; error: unknown }) => void;
  /** Override the retry classifier. Defaults to `isRetryableError`. */
  shouldRetry?: (error: unknown) => boolean;
}

/** Run `fn` with exponential backoff + jitter. Throws the LAST error
 *  if all attempts fail (or the first non-retryable error). */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  opts: RetryOptions = {},
): Promise<T> {
  const maxAttempts = Math.max(1, opts.maxAttempts ?? 3);
  const base = Math.max(50, opts.baseDelayMs ?? 400);
  const cap = Math.max(base, opts.maxDelayMs ?? 4000);
  const classify = opts.shouldRetry ?? isRetryableError;

  let lastErr: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      const exhausted = attempt >= maxAttempts;
      if (exhausted || !classify(err)) throw err;
      // Exponential backoff with full jitter: prevents thundering-herd
      // when many users retry the same edge function at once.
      const exp = Math.min(cap, base * 2 ** (attempt - 1));
      const delay = Math.floor(Math.random() * exp);
      opts.onRetry?.({ attempt, nextAttempt: attempt + 1, delayMs: delay, error: err });
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  // Unreachable, but keeps TS happy.
  throw lastErr;
}